<template>
  <div id="page-user-list">
    <div class="vx-card p-6 pt-3 mt-6 tabular-view-member" v-show="loaded">
      <vs-table stripe :data="paginatedItems">
        <template slot="header">
          <div class="flex justify-between items-center my-4 w-full">
            <!-- Search Input -->
            <vs-input v-model="searchQuery" :placeholder="$i18n.t('Search')"
                     class="mb-4 md:mb-0 md:mr-4 md:w-1/5" />

            <div class="flex justify-end items-center w-1/2 gap-2">
              <div class="flex items-center">
                <vs-button icon @click="prevMonth" :disabled="selectedMonth === '01'">‹</vs-button>
                <select 
                  v-model="selectedMonth" 
                  @change="handleSelectMonth"
                  class="mx-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  style="width: 150px;"
                >
                  <option v-for="(month, index) in months" :key="index" :value="month.value">
                    {{ month.label }}
                  </option>
                </select>
                <vs-button icon @click="nextMonth" :disabled="selectedMonth === '12'">›</vs-button>
              </div>
              <vs-button to="/journal-vouchers/add"  color="primary" size="" class="text-sm" type="filled">{{$i18n.t('AddNewData')}}</vs-button>
              <vs-dropdown>
                <vs-button color="success" type="filled" class="text-sm">
                  {{$i18n.t('PrintData')}}
                </vs-button>
                <vs-dropdown-menu class="w-48">
                  <vs-dropdown-item @click="exportPDF">
                    <i class="bx bxs-file-pdf mr-2 text-red-600"></i> Save as PDF
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="exportExcel">
                    <i class="bx bxs-file-export mr-2 text-green-600"></i>Export to Excel
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </div>
          </div>
        </template>
        <template slot="thead" v-if="paginatedItems.length > 0">
            <vs-th>No</vs-th>
            <vs-th>{{$i18n.t('VoucherNumber')}}</vs-th>
            <vs-th>{{$i18n.t('Date')}}</vs-th>
            <vs-th>{{$i18n.t('Description')}}</vs-th>
            <vs-th>{{$i18n.t('Items')}}</vs-th>
            <vs-th>{{$i18n.t('Debit')}}</vs-th>
            <vs-th>{{$i18n.t('Credit')}}</vs-th>
            <vs-th>Cashflow</vs-th>
            <vs-th class="journal-voucher-actions-cell">{{ $i18n.t('Actions') }}</vs-th>
        </template>

        <template slot-scope="{data}">
          <template v-for="(item, index) in data">
            <vs-tr :class="(item.type == 'KM') ? 'text-danger' : null" :key="index">
                <vs-td class="text-center" :data="item.no">
                  {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                </vs-td>
                <vs-td :data="item.no_voucher">
                  {{ item.no_voucher }}
                </vs-td>
                <vs-td :data="item.date">
                  {{ item.date }}
                </vs-td>
                <vs-td :data="item.description">
                  {{ item.description }}
                </vs-td>
                <vs-td :data="item.items" class="text-center">
                  {{ item.items }}
                </vs-td>
                <vs-td :data="item.debit" class="text-right">
                  {{ formatRupiah(item.debit) }}
                </vs-td>
                <vs-td :data="item.credit" class="text-right">
                  {{ formatRupiah(item.credit) }}
                </vs-td>
                <vs-td :data="item.cashflow_id" class="text-center">
                  {{ item.cashflow_id ? 'TERCATAT' : '-' }}
                </vs-td>
                <vs-td class="journal-voucher-actions-cell">
                  <div class="vx-row flex flex-no-wrap justify-center">
                    <vs-button size="small" class="mr-2" color="primary" type="filled" icon-pack="feather" icon="icon-eye" title="Preview voucher" :disabled="previewLoading && previewRequestedId === item.id" @click="openPreview(item)" />
                    <vs-button size="small" class="mr-2 px-3" color="success" type="filled" icon-pack="feather" icon="icon-edit" @click="editData(item)"></vs-button>
                    <vs-button size="small" class="px-3" color="danger" type="filled" icon-pack="feather" icon="icon-trash" @click="confirmDeleteRecord(item)"></vs-button>
                  </div>
                </vs-td>
            </vs-tr>
          </template>
          <vs-tr v-show="data.length > 0">
            <vs-td colspan="4" class="text-right font-semibold">Total</vs-td>
            <vs-td class="text-center font-semibold">
              {{ totalItem }}
            </vs-td>
            <vs-td class="text-right font-semibold">
              {{ formatRupiah(totalDebit)}}
            </vs-td>
            <vs-td class="text-right font-semibold">
              {{ formatRupiah(totalCredit) }}
            </vs-td>
            <vs-td></vs-td>
            <vs-td class="text-right text-primary" v-if="totalDebit == totalCredit">Balance</vs-td>
            <vs-td class="text-right text-danger" v-else>Not Balance</vs-td>
          </vs-tr>

          <vs-tr>
            <vs-td colspan="9" class="text-center font-semibold" v-if="paginatedItems.length > 0">
              <vs-pagination v-model="currentPage" :total="totalPages" :per-page="itemsPerPage"></vs-pagination>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </div>

    <vs-popup class="journal-voucher-preview-popup" title="Preview Voucher Jurnal" :active.sync="previewActive">
      <div v-if="previewVoucher" class="journal-voucher-preview">
        <div class="journal-voucher-preview__title">
          <h2>VOUCHER JURNAL</h2>
          <span
            class="journal-voucher-preview__status"
            :class="previewBalanced ? 'journal-voucher-preview__status--balanced' : 'journal-voucher-preview__status--unbalanced'"
          >
            {{ previewBalanced ? 'BALANCE' : 'TIDAK BALANCE' }}
          </span>
        </div>

        <div class="journal-voucher-preview__meta">
          <div><span>Nomor Voucher</span><strong>{{ previewVoucher.no_voucher || '-' }}</strong></div>
          <div><span>Tanggal</span><strong>{{ formatPreviewDate(previewVoucher.transaction_date) }}</strong></div>
          <div><span>Perusahaan</span><strong>{{ company && company.name ? company.name : '-' }}</strong></div>
        </div>

        <div class="cashflow-detail mt-5">
          <h3>Cashflow</h3>
          <p v-if="previewVoucher.cashflow_id" class="mt-3">
            Tercatat sebagai Cashflow {{ (previewVoucher.cashflow && previewVoucher.cashflow.number) || previewVoucher.cashflow_id }}.
          </p>
          <p v-else class="mt-3 text-grey">Voucher ini tidak dicatat ke Cashflow.</p>
        </div>

        <div class="journal-voucher-preview__table-wrap">
          <table class="journal-voucher-preview__table">
            <thead>
              <tr>
                <th class="journal-voucher-preview__number">No</th>
                <th>Akun</th>
                <th>Deskripsi</th>
                <th class="journal-voucher-preview__amount">Debit</th>
                <th class="journal-voucher-preview__amount">Kredit</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(transaction, index) in previewVoucher.transactions" :key="transaction.id || index">
                <td class="journal-voucher-preview__number">{{ index + 1 }}</td>
                <td>{{ accountDisplay(transaction) }}</td>
                <td class="journal-voucher-preview__description">{{ transaction.description || '-' }}</td>
                <td class="journal-voucher-preview__amount">{{ formatPreviewAmount(transaction.debit) }}</td>
                <td class="journal-voucher-preview__amount">{{ formatPreviewAmount(transaction.credit) }}</td>
              </tr>
              <tr v-if="previewVoucher.transactions.length === 0">
                <td colspan="5" class="journal-voucher-preview__empty">Tidak ada transaksi.</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="3" class="journal-voucher-preview__total-label">TOTAL</th>
                <th class="journal-voucher-preview__amount">{{ formatPreviewAmount(previewTotalDebit, false) }}</th>
                <th class="journal-voucher-preview__amount">{{ formatPreviewAmount(previewTotalCredit, false) }}</th>
              </tr>
            </tfoot>
          </table>
        </div>

        <p v-if="!previewBalanced" class="journal-voucher-preview__warning">
          Selisih debit dan kredit: {{ formatPreviewAmount(previewDifference, false) }}. Nilai ditampilkan sesuai data tersimpan.
        </p>
      </div>

    </vs-popup>
  </div>
</template>

<script>
import moment from 'moment'
import { AgGridVue } from 'ag-grid-vue'
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss'
import StatisticsCardLine from '@/components/statistics-cards/StatisticsCardLine.vue'
import vSelect from 'vue-select'

import moduleVoucher from '@/store/voucher/moduleVoucher.js'

// Cell Renderer
import CellRendererLink from './cell-renderer/CellRendererLink.vue'
import CellRendererStatus from './cell-renderer/CellRendererStatus.vue'
import CellRendererVerified from './cell-renderer/CellRendererVerified.vue'
import CellRendererActions from './cell-renderer/CellRendererActions.vue'

export default {
  components: {
    AgGridVue,
    vSelect,
    StatisticsCardLine,
    CellRendererLink,
    CellRendererStatus,
    CellRendererVerified,
    CellRendererActions
  },
  data () {
    return {
      currentPage: 1,
      startDate: moment(String(new Date(Number(localStorage.getItem('selectedYear')),  new Date().getMonth(), 1))).format('YYYY-MM-DD'),
      endDate: moment(String(new Date(Number(localStorage.getItem('selectedYear')), new Date().getMonth() + 1, 0))).format('YYYY-MM-DD'),
      loaded: false,
      vouchersData: [],
      previewActive: false,
      previewLoading: false,
      previewRequestedId: null,
      previewVoucher: null,
      itemsPerPage: 50,
      searchQuery: '',
      selectedMonth: String(new Date().getMonth() + 1).padStart(2, '0'),
      months: [
        { label: 'Januari', value: '01' },
        { label: 'Februari', value: '02' },
        { label: 'Maret', value: '03' },
        { label: 'April', value: '04' },
        { label: 'Mei', value: '05' },
        { label: 'Juni', value: '06' },
        { label: 'Juli', value: '07' },
        { label: 'Agustus', value: '08' },
        { label: 'September', value: '09' },
        { label: 'Oktober', value: '10' },
        { label: 'November', value: '11' },
        { label: 'Desember', value: '12' }
      ]
    }
  },
  methods: {
    async openPreview (voucher) {
      if (this.previewLoading) return

      this.previewLoading = true
      this.previewRequestedId = voucher.id
      this.previewActive = false
      this.previewVoucher = null

      try {
        const response = await this.$store.dispatch('vouchers/fetchVoucher', voucher.id)
        const detail = response && response.data ? response.data.data : null

        if (!detail || !Array.isArray(detail.transactions)) {
          throw new Error('Data voucher tidak lengkap.')
        }

        this.previewVoucher = detail
        await this.$nextTick()
        this.previewActive = true
      } catch (error) {
        const responseData = error && error.response && error.response.data
        this.$vs.notify({
          color: 'danger',
          title: 'Preview gagal',
          text: (responseData && responseData.message) || error.message || 'Voucher tidak dapat dimuat.'
        })
      } finally {
        this.previewLoading = false
        this.previewRequestedId = null
      }
    },
    accountDisplay (transaction) {
      const account = transaction.account

      if (account && (account.account_code || account.name)) {
        return [account.account_code, account.name].filter(Boolean).join(' - ')
      }

      return transaction.account_id ? `Akun #${transaction.account_id}` : '-'
    },
    numericAmount (value) {
      const amount = Number(value)
      return Number.isFinite(amount) ? amount : 0
    },
    formatPreviewAmount (value, dashForZero = true) {
      const amount = this.numericAmount(value)
      if (dashForZero && amount === 0) return '-'
      return new Intl.NumberFormat('id-ID').format(amount)
    },
    formatPreviewDate (value) {
      if (!value) return '-'
      const date = moment(value)
      return date.isValid() ? date.format('DD MMMM YYYY') : value
    },
    exportExcel () {
      this.$vs.loading({
        type: 'border',
        color: 'primary',
        text: 'Generating File Excel...'
      })

      const payload = {
        company_id: this.company.id,
        startDate: this.startDate,
        endDate: this.endDate,
        searchQuery: this.searchQuery
      }

      this.$store.dispatch('vouchers/exportExcel', payload)
        .then(() => {
          this.$vs.notify({
            title: 'Success',
            text: 'Excel generated successfully',
            color: 'success'
          })
        })
        .catch(error => {
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'Failed to generate Excel',
            color: 'danger'
          })
        })
        .finally(() => {
          this.$vs.loading.close()
        })

    },
    exportPDF () {
      this.$vs.loading({
        type: 'border',
        color: 'primary',
        text: 'Generating PDF...'
      })

      const payload = {
        company_id: this.company.id,
        startDate: this.startDate,
        endDate: this.endDate,
        searchQuery: this.searchQuery
      }

      this.$store.dispatch('vouchers/exportPDF', payload)
        .then(() => {
          this.$vs.notify({
            title: 'Success',
            text: 'PDF generated successfully',
            color: 'success'
          })
        })
        .catch(error => {
          this.$vs.notify({
            title: 'Error',
            text: error.message || 'Failed to generate PDF',
            color: 'danger'
          })
        })
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    loading () {
      this.loaded = false
      this.$vs.loading({
        type: 'border',
        color: 'primary',
        text: 'Wait a minute, It\'s getting data.'
      })
    },
    editData (data) {
      this.$router.push(`/journal-vouchers/edit/${data.id}`).catch(() => {})
    },
    confirmDeleteRecord (data) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: `You are about to delete "${data.no_voucher}"`,
        accept: () => this.deleteRecord(data.id),
        acceptText: 'Delete'
      })
    },
    deleteRecord (id) {
      this.$store.dispatch('vouchers/deleteVoucher', id)
        .then(() => {
          this.loading()
          this.getVouchers()
          this.$vs.notify({
            color: 'success',
            title: 'Account Deleted',
            text: 'The selected account was successfully deleted'
          })
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Opps..',
            text: err
          })
        })
    },
    getVouchers () {
      const self = this
      self.vouchersData = []

      this.$store.dispatch('vouchers/fetchVouchers', {
        company_id: this.company.id,
        startDate: self.startDate,
        endDate: self.endDate
      })
        .then(res => {
          const data = res.data.data

          if (data.length > 0) {
            data.forEach((value) => {
              const types = value.no_voucher.substring(0, 2)
              const debit = value.transactions.reduce((accum, item) => accum + parseInt(item.debit), 0)
              const credit = value.transactions.reduce((accum, item) => accum + parseInt(item.credit), 0)

              self.vouchersData.push({
                id: value.id,
                type: types,
                no_voucher: value.no_voucher,
                date: value.transaction_date,
                description: (value.transactions.length > 0) ? value.transactions[0].description : '-',
                items: value.transactions_count,
                debit,
                credit,
                cashflow_id: value.cashflow_id
              })
            })
          } else {
            self.vouchersData = []
          }
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Opps..',
            text: err
          })
        })
        .finally(() => {
          this.onLoaded()
        })
    },
    onLoaded () {
      this.loaded = true
      this.$vs.loading.close()
    },
    refreshData () {
      this.loading()
      this.getVouchers()
    },
    filterDate () {
      if (this.startDate > this.endDate) {
        this.$vs.notify({
          color: 'warning',
          title: 'Peringatan',
          text: 'Tanggal akhir tidak boleh sebelum tanggal awal'
        })
      } else {
        this.loading()
        this.getVouchers()
      }
    },
    filterDateThisYear () {
      this.startDate = moment().startOf('year').format('YYYY-MM-DD')
      this.endDate = moment().endOf('year').format('YYYY-MM-DD')
      this._applyFilter()
    },
    filterDateLastYear () {
      this.startDate = moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD')
      this.endDate = moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD')
      this._applyFilter()
    },
    _applyFilter () {
      this.loading()
      this.getVouchers()
    },
    formatRupiah (value) {
      const val = (value / 1).toFixed(0).replace('.', ',')
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    prevMonth () {
      const currentIndex = this.months.findIndex(m => m.value === this.selectedMonth)
      if (currentIndex > 0) {
        this.selectedMonth = this.months[currentIndex - 1].value
        this.handleSelectMonth()
      }
    },
    nextMonth () {
      const currentIndex = this.months.findIndex(m => m.value === this.selectedMonth)
      if (currentIndex < this.months.length - 1) {
        this.selectedMonth = this.months[currentIndex + 1].value
        this.handleSelectMonth()
      }
    },
    handleSelectMonth () {
      const selectedYear = localStorage.getItem('selectedYear') || moment().year()
      this.startDate = moment(`${selectedYear}-${this.selectedMonth}-01`).format('YYYY-MM-DD')
      this.endDate = moment(`${selectedYear}-${this.selectedMonth}-01`).endOf('month').format('YYYY-MM-DD')
      this.currentPage = 1
      this.loading()
      this.getVouchers()
    }
  },
  computed: {
    filteredItems () {
      const searchTerm = this.searchQuery.toLowerCase()
      return this.vouchersData.filter(item => {
        return (
          (item.no_voucher && item.no_voucher.toLowerCase().includes(searchTerm)) ||
          (item.description && item.description.toLowerCase().includes(searchTerm)) ||
          (item.date && item.date.toLowerCase().includes(searchTerm))
        )
      })
    },
    paginatedItems () {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredItems.slice(start, end)
    },
    totalPages () {
      return Math.ceil(this.filteredItems.length / this.itemsPerPage)
    },
    totalItem () {
      return this.filteredItems.reduce((acc, item) => acc + parseInt(item.items || 0), 0)
    },
    totalDebit () {
      return this.filteredItems.reduce((acc, item) => acc + parseInt(item.debit || 0), 0)
    },
    totalCredit () {
      return this.filteredItems.reduce((acc, item) => acc + parseInt(item.credit || 0), 0)
    },
    previewTotalDebit () {
      if (!this.previewVoucher) return 0
      return this.previewVoucher.transactions.reduce((total, transaction) => {
        return total + this.numericAmount(transaction.debit)
      }, 0)
    },
    previewTotalCredit () {
      if (!this.previewVoucher) return 0
      return this.previewVoucher.transactions.reduce((total, transaction) => {
        return total + this.numericAmount(transaction.credit)
      }, 0)
    },
    previewDifference () {
      return Math.abs(this.previewTotalDebit - this.previewTotalCredit)
    },
    previewBalanced () {
      return this.previewTotalDebit === this.previewTotalCredit
    },
    company () {
      return JSON.parse(localStorage.getItem('activeCompany'))
    }
  },
  watch: {
    searchQuery () {
      this.currentPage = 1 // Reset to first page when search changes
    },
    previewActive (active) {
      if (!active && !this.previewLoading) this.previewVoucher = null
    }
  },
  mounted () {
    // Initialize if needed
  },
  created () {
    if (!moduleVoucher.isRegistered) {
      this.$store.registerModule('vouchers', moduleVoucher)
      moduleVoucher.isRegistered = true
    }
    this.loading()
    this.getVouchers()
  }
}
</script>

<style lang="scss">
.journal-voucher-actions-cell {
  min-width: 180px;
  white-space: nowrap;
}

.cashflow-detail {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
}

.filter-date {
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #111;
  border-radius: 5px;
  padding: 10px;
}

.ag-grid-table {
  height: 100%
}

.ag-row .ag-cell {
  display: flex;
  align-items: center;
}

.vs-table th {
  font-size: 14px;
  font-weight: 500;
  padding: 20px !important;
}

.vs-table th:nth-child(2) {
  min-width: 150px !important
}

.vs-table th:nth-child(3) {
  min-width: 110px !important
}

.vs-table th:nth-child(6) .vs-table-text {
  justify-content: end
}

.vs-table th:nth-child(7) .vs-table-text {
  justify-content: end
}

.vs-table th:last-child {
  padding: 0 30px !important
}

.vs-table-actions {
  display: flex;
}

.vs-table--search {
  position: absolute !important
}

.vs-table--search input {
  border-radius: 5px;
}

.journal-voucher-preview-popup .vs-popup {
  width: 1000px;
  max-width: 95vw;
}

.journal-voucher-preview {
  color: #1f2937;
  background: #fff;
  padding: 8px;
}

.journal-voucher-preview__title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid #374151;
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.journal-voucher-preview__title h2 {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.journal-voucher-preview__status {
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
}

.journal-voucher-preview__status--balanced {
  color: var(--app-primary-ink);
  background: var(--app-primary-soft);
}

.journal-voucher-preview__status--unbalanced {
  color: var(--app-secondary-ink);
  background: var(--app-secondary-soft);
}

.journal-voucher-preview__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.journal-voucher-preview__meta div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.journal-voucher-preview__meta span {
  color: #6b7280;
  font-size: 12px;
}

.journal-voucher-preview__table-wrap {
  overflow-x: auto;
}

.journal-voucher-preview__table {
  width: 100%;
  border-collapse: collapse;
}

.journal-voucher-preview__table th,
.journal-voucher-preview__table td {
  border: 1px solid #d1d5db;
  padding: 10px;
  vertical-align: top;
}

.journal-voucher-preview__table thead,
.journal-voucher-preview__table tfoot {
  background: #f3f4f6;
}

.journal-voucher-preview__number {
  width: 52px;
  text-align: center;
}

.journal-voucher-preview__description {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.journal-voucher-preview__amount {
  width: 150px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.journal-voucher-preview__total-label {
  text-align: right;
}

.journal-voucher-preview__empty {
  text-align: center;
  color: #6b7280;
}

.journal-voucher-preview__warning {
  margin-top: 14px;
  padding: 10px 12px;
  color: var(--app-secondary-ink);
  background: #fef2f2;
  border-left: 4px solid var(--app-secondary);
}

@media (max-width: 767px) {
  .journal-voucher-preview__meta {
    grid-template-columns: 1fr;
  }
}

</style>
